// client/src/features/salesCoach/components/CreateEditTraining.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { salesCoachApi } from "../api/salesCoachApi";
import { ArrowLeft, Save, Sparkles, BookOpen, Settings, ShieldAlert, Brain } from "lucide-react";
import Loader from "@/shared/components/ui/Loaders/Loader";
import MemoryRules from "./MemoryRules";

const CreateEditTraining = ({ tab, id }) => {
  const navigate = useNavigate();
  const isEdit = tab === "edit";

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState("basic"); // 'basic' | 'knowledge' | 'rules'

  // Form State
  const [productName, setProductName] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("draft");

  // Product Knowledge Fields
  const [knowledge, setKnowledge] = useState({
    product_overview: "",
    key_features: "",
    key_benefits: "",
    target_customers: "",
    competitive_advantages: "",
    competitor_comparison: "",
    common_customer_objections: "",
    objection_handling_guidelines: "",
    sales_pitch: "",
    faqs: "",
    additional_notes: "",
  });

  useEffect(() => {
    if (isEdit && id) {
      loadTrainingData();
    }
  }, [id]);

  const loadTrainingData = async () => {
    setLoading(true);
    try {
      const data = await salesCoachApi.getTraining(id);
      if (data) {
        setProductName(data.product_name || "");
        setCategory(data.category || "");
        setDescription(data.description || "");
        setStatus(data.status || "draft");
        if (data.content) {
          setKnowledge({
            product_overview: data.content.product_overview || "",
            key_features: data.content.key_features || "",
            key_benefits: data.content.key_benefits || "",
            target_customers: data.content.target_customers || "",
            competitive_advantages: data.content.competitive_advantages || "",
            competitor_comparison: data.content.competitor_comparison || "",
            common_customer_objections:
              data.content.common_customer_objections || "",
            objection_handling_guidelines:
              data.content.objection_handling_guidelines || "",
            sales_pitch: data.content.sales_pitch || "",
            faqs: data.content.faqs || "",
            additional_notes: data.content.additional_notes || "",
          });
        }
      }
    } catch (err) {
      console.error("Failed to load training details:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleKnowledgeChange = (field, val) => {
    setKnowledge((prev) => ({
      ...prev,
      [field]: val,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!productName || !category) {
      alert("Product Name and Category are required");
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        await salesCoachApi.updateTraining(id, {
          productName,
          category,
          description,
          status,
          content: knowledge,
        });
      } else {
        const newTraining = await salesCoachApi.createTraining({
          productName,
          category,
          description,
          status,
        });
        if (newTraining?.id) {
          await salesCoachApi.updateTraining(newTraining.id, {
            content: knowledge,
          });
        }
      }
      navigate("/oa/sales-coach");
    } catch (err) {
      console.error("Failed to save training:", err);
      alert("Error saving training. Please check backend connections.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <Loader text="Loading training details..." />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto min-h-[calc(100vh-60px)] bg-transparent">
      {/* Back nav & title */}
      <div className="flex items-center gap-4 pb-4">
        <Link
          to="/oa/sales-coach"
          className="p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-gray-100"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {isEdit
              ? `Edit Training: ${productName}`
              : "Create Product Training"}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {isEdit
              ? "Update training specifications and coach configuration"
              : "Fill out details to initialize a training program"}
          </p>
        </div>
      </div>

      {/* Tabs list */}
      <div className="flex border-b border-gray-200 dark:border-gray-800 mt-6 mb-6">
        <button
          onClick={() => setActiveSubTab("basic")}
          className={`py-3 px-4 text-sm font-semibold border-b-2 transition-all ${
            activeSubTab === "basic"
              ? "border-secondary text-secondary dark:text-secondary-light"
              : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          }`}
        >
          <Settings className="w-4 h-4 inline mr-2" />
          Basic Details
        </button>
        <button
          onClick={() => setActiveSubTab("knowledge")}
          className={`py-3 px-4 text-sm font-semibold border-b-2 transition-all ${
            activeSubTab === "knowledge"
              ? "border-secondary text-secondary dark:text-secondary-light"
              : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          }`}
        >
          <BookOpen className="w-4 h-4 inline mr-2" />
          Product Knowledge (RAG Database)
        </button>
        {isEdit && (
          <button
            onClick={() => setActiveSubTab("rules")}
            className={`py-3 px-4 text-sm font-semibold border-b-2 transition-all ${
              activeSubTab === "rules"
                ? "border-secondary text-secondary dark:text-secondary-light"
                : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            }`}
          >
            <ShieldAlert className="w-4 h-4 inline mr-2" />
            Coaching Rules
          </button>
        )}
      </div>

      {activeSubTab === "rules" ? (
        <div className="space-y-6 pb-8">
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              View and edit coaching guidelines used during rep learn and roleplay sessions.
            </p>
            <Link
              to={`/oa/sales-coach/trainer/${id}`}
              className="inline-flex items-center px-4 py-2 text-sm font-semibold text-secondary dark:text-secondary-light bg-secondary-ultra-light dark:bg-secondary/20 border border-[#207C9730] rounded-xl hover:bg-secondary-ultra-light/80 transition-colors whitespace-nowrap"
            >
              <Brain className="w-4 h-4 mr-2" />
              Train Coach
            </Link>
          </div>
          <MemoryRules trainingId={id} />
        </div>
      ) : (
      <form onSubmit={handleSubmit} className="space-y-6">
        {activeSubTab === "basic" ? (
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-6 shadow-sm space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200">
                  Product Name
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. SalesWorx Copilot"
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                  className="mt-2 block w-full rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-2.5 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:border-secondary focus:outline-none focus:ring-1 focus:ring-secondary text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200">
                  Category
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. SaaS / Software"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="mt-2 block w-full rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-2.5 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:border-secondary focus:outline-none focus:ring-1 focus:ring-secondary text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200">
                Description
              </label>
              <textarea
                rows={4}
                placeholder="Brief summary of what this training represents..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="mt-2 block w-full rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-2.5 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:border-secondary focus:outline-none focus:ring-1 focus:ring-secondary text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200">
                Publishing Status
              </label>
              <div className="flex gap-4 mt-3">
                <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="radio"
                    name="status"
                    value="draft"
                    checked={status === "draft"}
                    onChange={() => setStatus("draft")}
                    className="h-4 w-4 text-secondary focus:ring-secondary"
                  />
                  <span>Draft (Hidden from Reps)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="radio"
                    name="status"
                    value="published"
                    checked={status === "published"}
                    onChange={() => setStatus("published")}
                    className="h-4 w-4 text-secondary focus:ring-secondary"
                  />
                  <span className="font-semibold text-green-700 dark:text-green-400">
                    Published (Visible to Reps)
                  </span>
                </label>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-6 shadow-sm space-y-6">
            <div className="flex items-center gap-2 p-4 bg-secondary-ultra-light/50 dark:bg-secondary/20 rounded-xl border border-[#207C9720] text-secondary dark:text-secondary-light">
              <Sparkles className="w-5 h-5 flex-shrink-0 text-secondary dark:text-secondary-light" />
              <p className="text-xs">
                This content will be vectorized and indexed. The AI Sales Coach
                will answer questions based <strong>only</strong> on what is
                written below.
              </p>
            </div>

            <div className="grid gap-6">
              {[
                {
                  label: "Product Overview",
                  field: "product_overview",
                  placeholder:
                    "General description of the product, service, and core value proposition...",
                },
                {
                  label: "Key Features",
                  field: "key_features",
                  placeholder:
                    "Bullet points detailing major capabilities, technology, or specs...",
                },
                {
                  label: "Key Benefits",
                  field: "key_benefits",
                  placeholder:
                    "Customer value propositions, ROI calculators, time saved, or efficiency gains...",
                },
                {
                  label: "Target Customers",
                  field: "target_customers",
                  placeholder:
                    "Ideal customer profiles (ICP), target industries, persona triggers, buyer profiles...",
                },
                {
                  label: "Competitive Advantages",
                  field: "competitive_advantages",
                  placeholder:
                    "Why customers should choose this product over alternatives, specific key differentiators...",
                },
                {
                  label: "Competitor Comparison",
                  field: "competitor_comparison",
                  placeholder:
                    "How this compares with major competitors, pros/cons, positioning statements...",
                },
                {
                  label: "Common Customer Objections",
                  field: "common_customer_objections",
                  placeholder:
                    "Pricing objections, security concerns, implementation/timing doubts...",
                },
                {
                  label: "Objection Handling Guidelines",
                  field: "objection_handling_guidelines",
                  placeholder:
                    "Scripted suggestions or strategies on how representatives should address these concerns...",
                },
                {
                  label: "Sales Pitch Template",
                  field: "sales_pitch",
                  placeholder:
                    "Suggested elevator pitch or starting statement for sales reps...",
                },
                {
                  label: "Frequently Asked Questions (FAQs)",
                  field: "faqs",
                  placeholder: "Questions and exact recommended answers...",
                },
                {
                  label: "Additional Training Notes",
                  field: "additional_notes",
                  placeholder:
                    "Any internal memos, warranty details, pricing plans, setup checklists, etc...",
                },
              ].map(({ label, field, placeholder }) => (
                <div key={field}>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200">
                    {label}
                  </label>
                  <textarea
                    rows={4}
                    placeholder={placeholder}
                    value={knowledge[field]}
                    onChange={(e) =>
                      handleKnowledgeChange(field, e.target.value)
                    }
                    className="mt-2 block w-full rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-2.5 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:border-secondary focus:outline-none focus:ring-1 focus:ring-secondary text-sm font-normal"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 pb-8">
          <Link
            to="/oa/sales-coach"
            className="px-5 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none transition-colors"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center px-6 py-2.5 text-sm font-semibold text-white bg-secondary rounded-xl hover:bg-secondary-light focus:outline-none focus:ring-2 focus:ring-secondary transition-all shadow-md hover:shadow-[#207C9730]"
          >
            {saving ? (
              <>
                <Loader className="mr-2" size="sm" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Training Content
              </>
            )}
          </button>
        </div>
      </form>
      )}
    </div>
  );
};

export default CreateEditTraining;
